import React, { useState, useMemo, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classNames from "classnames";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import {
  useGetPortfolioDetail,
  useUpdatePortfolio,
} from "api/portfolios/service";
import { useGetAccount } from "api/account/service";
import PortfolioDetail from "../../../components/Portfolios/PortfolioDetail";
import PortfolioInvestors from "../../../components/Portfolios/PortfolioInvestors";
import PortfolioPastDueFilters from "../../../components/Portfolios/PortfolioPastDueFilters";
import PortfolioNotifications from "../../../components/Portfolios/PortfolioNotifications";
import PortfolioEpsPpd from "../../../components/Portfolios/PortfolioEpsPpd";
import PortfolioSideNoteSubTypes from "../../../components/Portfolios/PortfolioSideNoteSubTypes";
import NotesTab from "views/inventory/tabs/NotesTab";
import SubHeader from "components/Navbars/SubHeader";

const EditPortfolios = ({
  activeColor = "secondary",
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const { id } = useParams();
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const isTheme2 = shellThemeVariant === "2";
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".edit-portfolio-page.edit-portfolio-page--theme2",
            fieldsSelector: ".edit-portfolio-form-fields",
          })
        : "",
    [isTheme2]
  );
  const [activeTab, setActiveTab] = useState("DETAIL");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const { data } = useGetPortfolioDetail(id);
  const { data: account } = useGetAccount();
  const updatePortfolioMutation = useUpdatePortfolio();
  const [isSaving, setIsSaving] = useState(false);

  // Tab refs
  const detailTabRef = useRef();
  const investorsTabRef = useRef();
  const pastDueFiltersTabRef = useRef();
  const notificationsTabRef = useRef();
  const epsPpdTabRef = useRef();
  const sideNoteSubTypesTabRef = useRef();

  const tabs = [
    { id: "DETAIL", label: "DETAIL" },
    { id: "INVESTORS", label: "INVESTORS" },
    { id: "PAST_DUE_FILTERS", label: "PAST DUE FILTERS" },
    { id: "NOTIFICATIONS", label: "NOTIFICATIONS" },
    { id: "EPS_PPD", label: "EPS / PPD PAYMENT INSTRUCTIONS" },
    { id: "SIDE_NOTE_SUB_TYPES", label: "SIDE NOTE SUB-TYPES" },
    { id: "NOTES", label: "NOTES" },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const portfolioTitle = useMemo(() => {
    if (data?.code && data?.name) {
      return `${data.code} - ${data.name}`;
    }
    return data?.name || data?.code || id;
  }, [data?.code, data?.name, id]);

  const portfolioStatus = useMemo(() => {
    if (data?.active === true) return "Active";
    if (data?.active === false) return "Inactive";
    return "";
  }, [data?.active]);

  const portfolioStatusColor = useMemo(() => {
    if (data?.active === true) return "#28a745";
    if (data?.active === false) return "#dc3545";
    return undefined;
  }, [data?.active]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // Save fonksiyonu
  const handleSave = async () => {
    setIsSaving(true);
    try {
      let result = { success: false };

      // Aktif tab'a göre save işlemi yap
      if (activeTab === "DETAIL" && detailTabRef.current) {
        result = await detailTabRef.current.handleSave();
      }
      if (activeTab === "INVESTORS" && investorsTabRef.current) {
        result = await investorsTabRef.current.handleSave();
      }
      if (activeTab === "PAST_DUE_FILTERS" && pastDueFiltersTabRef.current) {
        result = await pastDueFiltersTabRef.current.handleSave();
      }
      if (activeTab === "NOTIFICATIONS" && notificationsTabRef.current) {
        result = await notificationsTabRef.current.handleSave();
      }
      if (activeTab === "EPS_PPD" && epsPpdTabRef.current) {
        result = await epsPpdTabRef.current.handleSave();
      }
      if (
        activeTab === "SIDE_NOTE_SUB_TYPES" &&
        sideNoteSubTypesTabRef.current
      ) {
        result = await sideNoteSubTypesTabRef.current.handleSave();
      }
    } catch (error) {
      console.log("EditPortfolios - error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const showSaveButton =
    account?.permissions?.settings_portfolios_edit &&
    (activeTab === "DETAIL" ||
      activeTab === "INVESTORS" ||
      activeTab === "PAST_DUE_FILTERS" ||
      activeTab === "NOTIFICATIONS" ||
      activeTab === "EPS_PPD" ||
      activeTab === "SIDE_NOTE_SUB_TYPES");

  const saveButton = showSaveButton ? (
    <button
      className="btn btn-sm"
      style={{ ...activeColorStyle, color: "white" }}
      onClick={handleSave}
      disabled={isSaving}
    >
      {isSaving ? (
        <>
          <span
            className="spinner-border spinner-border-sm mr-2"
            role="status"
            aria-hidden="true"
          />
          Saving...
        </>
      ) : (
        <>Save</>
      )}
    </button>
  ) : null;

  return (
    <div
      className={classNames("content", "edit-portfolio-page", {
        "edit-portfolio-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-portfolios-inset": isTheme2,
      })}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
      }}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div style={{ width: "100%" }}>
        <SubHeader
          activeColor={activeColor}
          back={-1}
          backFallback="/portfolios"
          title={portfolioTitle}
          status={portfolioStatus}
          statusColor={portfolioStatusColor}
          theme2ScrollGapFill={isTheme2}
          isLightTheme={isLightTheme}
        />
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              {/* Tabs Navigation */}
              <div className="w-100 portfolio-detail-tabs">
                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                  <div className="overflow-auto flex-grow-1 min-width-0">
                    <Nav
                      tabs
                      className="border-0 flex-nowrap"
                      style={{ borderBottom: "none" }}
                    >
                      {tabs.map((tab) => (
                        <NavItem key={tab.id} className="text-nowrap">
                          <NavLink
                            href="#"
                            className={
                              activeTab === tab.id
                                ? "active font-weight-bold"
                                : "text-muted"
                            }
                            style={{
                              background: "none",
                              border: "none",
                              position: "relative",
                              cursor: "pointer",
                              color:
                                activeTab === tab.id
                                  ? activeColorStyle.borderColor
                                  : undefined,
                              fontWeight: activeTab === tab.id ? 700 : 500,
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTabChange(tab.id);
                            }}
                          >
                            {tab.label}
                            {activeTab === tab.id && (
                              <div
                                style={{
                                  height: 3,
                                  background: activeColorStyle.borderColor,
                                  position: "absolute",
                                  left: 0,
                                  right: 0,
                                  bottom: -8,
                                }}
                              />
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  </div>

                  {showSaveButton ? (
                    <div className="d-none d-lg-flex align-items-center flex-shrink-0 ml-2">
                      {saveButton}
                    </div>
                  ) : null}

                  {/* Mobile View Buttons */}
                  {showSaveButton ? (
                    <div className="d-flex d-lg-none flex-column w-100 mt-2 mb-3">
                      <button
                        className="btn btn-sm w-100"
                        style={{ ...activeColorStyle, color: "white", padding: "12px" }}
                        onClick={handleSave}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm mr-2"
                              role="status"
                              aria-hidden="true"
                            />
                            Saving...
                          </>
                        ) : (
                          <>Save</>
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>

                {/* Tab Content */}
                <TabContent activeTab={activeTab}>
                  <TabPane
                    tabId="DETAIL"
                    className={classNames({ "edit-portfolio-form-fields": isTheme2 })}
                  >
                    <PortfolioDetail
                      ref={detailTabRef}
                      data={data}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                      portfolioId={id}
                      updateMutation={updatePortfolioMutation}
                    />
                  </TabPane>

                  <TabPane
                    tabId="INVESTORS"
                    className={classNames({ "edit-portfolio-form-fields": isTheme2 })}
                  >
                    <PortfolioInvestors
                      ref={investorsTabRef}
                      data={data?.loan_investor}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                      portfolioId={id}
                      updateMutation={updatePortfolioMutation}
                    />
                  </TabPane>

                  <TabPane
                    tabId="PAST_DUE_FILTERS"
                    className={classNames({ "edit-portfolio-form-fields": isTheme2 })}
                  >
                    <PortfolioPastDueFilters
                      ref={pastDueFiltersTabRef}
                      data={data}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                      portfolioId={id}
                      updateMutation={updatePortfolioMutation}
                    />
                  </TabPane>

                  <TabPane
                    tabId="NOTIFICATIONS"
                    className={classNames({ "edit-portfolio-form-fields": isTheme2 })}
                  >
                    <PortfolioNotifications
                      ref={notificationsTabRef}
                      data={data}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                      portfolioId={id}
                      updateMutation={updatePortfolioMutation}
                    />
                  </TabPane>

                  <TabPane
                    tabId="EPS_PPD"
                    className={classNames({ "edit-portfolio-form-fields": isTheme2 })}
                  >
                    <PortfolioEpsPpd
                      ref={epsPpdTabRef}
                      data={data?.payment_instructions}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                      portfolioId={id}
                      updateMutation={updatePortfolioMutation}
                    />
                  </TabPane>

                  <TabPane tabId="SIDE_NOTE_SUB_TYPES">
                    <PortfolioSideNoteSubTypes
                      ref={sideNoteSubTypesTabRef}
                      data={data}
                      activeColor={activeColor}
                      portfolioId={id}
                      updateMutation={updatePortfolioMutation}
                    />
                  </TabPane>

                  <TabPane tabId="NOTES">
                    <NotesTab
                      module="portfolio"
                      id={data?.id}
                      permission={{
                        notes_add: account?.permissions?.portfolios_notes_add,
                      }}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                    />
                  </TabPane>
                </TabContent>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      </div>
    </div>
  );
};

export default EditPortfolios;
